import { supabase } from "@/integrations/supabase/client";

/**
 * Resolves the apartment ID for a user.
 * Checks users.apartment_id first, then falls back to user_apartments table
 * so cotenants (secondary tenants) see the same data as primary tenants.
 */
export async function getUserApartmentId(userId: string): Promise<string | null> {
  // 1. Check direct assignment on users table
  const { data: userData } = await supabase
    .from("users")
    .select("apartment_id")
    .eq("id", userId)
    .maybeSingle();

  if (userData?.apartment_id) {
    return userData.apartment_id;
  }

  // 2. Fallback: check user_apartments table (cotenant)
  const { data: ua } = await supabase
    .from("user_apartments")
    .select("apartment_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (ua?.apartment_id) {
    return ua.apartment_id;
  }

  // 3. Fallback: check unit_owners table (owner role)
  const { data: uo } = await supabase
    .from("unit_owners")
    .select("apartment_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  return uo?.apartment_id ?? null;
}

export type ApartmentMeterRow = {
  id: string;
  meter_number?: string | null;
  sts_meter_no?: string | null;
  compliance_type?: string | null;
  deveui?: string | null;
  overdraft_limit_kwh?: number | null;
  last_reading_at?: string | null;
  manufacturers?: { slug: string } | null;
};

/**
 * Resolve the smart meter for an apartment.
 * Prefer apartments.smart_meter_id when set; otherwise the meter with the
 * newest last_reading_at. Uses limit(1) so multi-meter apartments do not
 * trip PostgREST maybeSingle() errors.
 */
export async function getApartmentSmartMeter(
  apartmentId: string,
  select = "id, meter_number, sts_meter_no, compliance_type, deveui, overdraft_limit_kwh, last_reading_at, manufacturers(slug)",
): Promise<ApartmentMeterRow | null> {
  const { data: apartmentRow } = await supabase
    .from("apartments")
    .select("smart_meter_id")
    .eq("id", apartmentId)
    .maybeSingle();

  if (apartmentRow?.smart_meter_id) {
    const { data: byFk } = await supabase
      .from("smart_meters")
      .select(select)
      .eq("id", apartmentRow.smart_meter_id)
      .maybeSingle();
    if (byFk) return byFk as unknown as ApartmentMeterRow;
  }

  const { data: rows, error } = await supabase
    .from("smart_meters")
    .select(select)
    .eq("apartment_id", apartmentId)
    .order("last_reading_at", { ascending: false, nullsFirst: false })
    .limit(1);

  if (error) {
    console.error("[getApartmentSmartMeter]", error);
    return null;
  }

  return (rows?.[0] as unknown as ApartmentMeterRow) ?? null;
}

export type UnitTopupRow = {
  id: string;
  amount: number;
  reference: string;
  created_at: string;
  completed_at: string | null;
  user_id?: string;
  meter_id?: string | null;
  meter_command_id?: string | null;
  [key: string]: unknown;
};

/**
 * Completed top-ups for the tenant's unit meter (all payers on that meter).
 * Falls back to the caller's own top-ups when no apartment/meter can be resolved.
 */
export async function fetchUnitCompletedTopups(opts: {
  userId: string;
  viewingApartmentId?: string | null;
  limit?: number;
  select?: string;
}): Promise<{ data: UnitTopupRow[]; error: Error | null; meterId: string | null }> {
  const limit = opts.limit ?? 50;
  const select =
    opts.select ??
    "id, amount, reference, created_at, completed_at, user_id, meter_id, meter_command_id";

  const apartmentId =
    opts.viewingApartmentId ?? (await getUserApartmentId(opts.userId));

  let meterId: string | null = null;
  if (apartmentId) {
    const meter = await getApartmentSmartMeter(apartmentId, "id");
    meterId = meter?.id ?? null;
  }

  if (meterId) {
    const { data, error } = await supabase
      .from("user_topups")
      .select(select)
      .eq("meter_id", meterId)
      .eq("status", "completed")
      .order("created_at", { ascending: false })
      .limit(Math.max(limit * 2, 20));

    if (error) {
      return { data: [], error: new Error(error.message), meterId };
    }

    const sorted = [...(data || [])]
      .map((tx: any) => ({
        ...tx,
        effective_at: tx.completed_at || tx.created_at,
      }))
      .sort(
        (a, b) =>
          new Date(b.effective_at).getTime() - new Date(a.effective_at).getTime()
      )
      .slice(0, limit) as UnitTopupRow[];

    return { data: sorted, error: null, meterId };
  }

  // No meter linked — show only this user's own history
  const { data, error } = await supabase
    .from("user_topups")
    .select(select)
    .eq("user_id", opts.userId)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(Math.max(limit * 2, 20));

  if (error) {
    return { data: [], error: new Error(error.message), meterId: null };
  }

  const sorted = [...(data || [])]
    .map((tx: any) => ({
      ...tx,
      effective_at: tx.completed_at || tx.created_at,
    }))
    .sort(
      (a, b) =>
        new Date(b.effective_at).getTime() - new Date(a.effective_at).getTime()
    )
    .slice(0, limit) as UnitTopupRow[];

  return { data: sorted, error: null, meterId: null };
}
